import { useMemo, useState } from 'react';
import {
  useReactTable,
  getCoreRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  flexRender,
} from '@tanstack/react-table';
import PropTypes from 'prop-types';
import '../../assets/styles/react-table.css';

const translations = {
  previousText: '<',
  nextText: '>',
  loadingText: 'Cargando..',
  ofText: 'de',
  rowsText: 'líneas',
  noDataText: 'Datos no encontrados',
  pageText: 'Página',
};

function TableCellRenderer({ original, value, row, render }) {
  return render({ original, value, row });
}
TableCellRenderer.propTypes = {
  original: PropTypes.object.isRequired,
  value: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
    PropTypes.bool,
    PropTypes.object,
  ]),
  row: PropTypes.shape({ original: PropTypes.object }).isRequired,
  render: PropTypes.func.isRequired,
};

const Table = ({
  columns = [],
  data = [],
  defaultPageSize = 10,
  showPaginationBottom = true,
  className = '',
  getTrProps = null,
  onLoadMore = null,
  isLoadingMore = false,
  hasMore = false,
}) => {
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: defaultPageSize,
  });
  const [columnSizing, setColumnSizing] = useState({});

  const tanstackColumns = useMemo(() => {
    return columns.map((col, index) => {
      let accessorFn;

      if (typeof col.accessor === 'function') {
        accessorFn = ({ row }) => col.accessor(row.original);
      }

      const columnId =
        col.id || (typeof col.accessor === 'string' ? col.accessor : undefined) || `col_${index}`;

      const hasCellRenderer = col.Cell || col.CellComponent;
      const getCellValue = (row) => {
        let value;
        if (col.accessor) {
          if (typeof col.accessor === 'string') {
            value = Object.prototype.hasOwnProperty.call(row.original, col.accessor)
              ? row.original[col.accessor]
              : undefined;
          } else if (typeof col.accessor === 'function') {
            try {
              value = col.accessor(row.original);
            } catch (e) {
              value = undefined;
            }
          }
        }
        return value;
      };

      const column = {
        id: columnId,
        accessorKey: hasCellRenderer
          ? undefined
          : typeof col.accessor === 'string'
          ? col.accessor
          : undefined,
        accessorFn: hasCellRenderer ? undefined : accessorFn,
        header: col.Header,
        cell: col.CellComponent
          ? ({ row }) => {
              const value = getCellValue(row);
              const { CellComponent } = col;
              return (
                <CellComponent
                  original={row.original}
                  value={value}
                  row={{ original: row.original }}
                  {...(col.cellProps || {})}
                />
              );
            }
          : col.Cell
          ? ({ row }) => {
              if (typeof col.Cell === 'function') {
                const value = getCellValue(row);
                return (
                  <TableCellRenderer
                    original={row.original}
                    value={value}
                    row={{ original: row.original }}
                    render={col.Cell}
                  />
                );
              }
              return col.Cell;
            }
          : accessorFn
          ? ({ row }) => col.accessor(row.original)
          : ({ getValue }) => {
              const value = getValue();

              if (value && typeof value === 'object') {
                if (value.$$typeof || (value.type && typeof value.type === 'function')) {
                  return value;
                }
                if (value.toString && value.toString() !== '[object Object]') {
                  return value.toString();
                }
              }

              return value != null ? String(value) : '';
            },
      };

      if (col.sortable === false) {
        column.enableSorting = false;
      }
      if (col.filterable === false) {
        column.enableColumnFilter = false;
      }
      if (col.width) {
        column.size = col.width;
      }

      if (col.resizable !== false) {
        column.enableResizing = true;
      }

      return column;
    });
  }, [columns]);

  const [sorting, setSorting] = useState([]);

  const table = useReactTable({
    data,
    columns: tanstackColumns,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    state: {
      pagination,
      columnSizing,
      sorting,
    },
    onSortingChange: setSorting,
    onPaginationChange: setPagination,
    onColumnSizingChange: setColumnSizing,
    columnResizeMode: 'onChange',
    manualPagination: false,
    defaultColumn: {
      minSize: 50,
      maxSize: 300,
      size: 100,
    },
  });

  return (
    <div className={`rt-table-container ${className}`.trim()}>
      <div className="rt-table-wrapper">
        <table className={`rt-table`.trim()}>
          <thead>
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <th
                    key={header.id}
                    style={{
                      width: header.getSize(),
                      position: 'relative',
                      cursor: header.column.getCanSort() ? 'pointer' : 'default',
                      userSelect: header.column.getCanSort() ? 'none' : 'auto',
                    }}
                    onClick={header.column.getToggleSortingHandler()}
                  >
                    {header.isPlaceholder
                      ? null
                      : flexRender(header.column.columnDef.header, header.getContext())}
                    {header.column.getIsSorted() === 'asc' ? ' ▲' : header.column.getIsSorted() === 'desc' ? ' ▼' : ''}
                    {header.column.getCanResize() && (
                      <div
                        role="button"
                        tabIndex={0}
                        onMouseDown={header.getResizeHandler()}
                        onTouchStart={header.getResizeHandler()}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            header.getResizeHandler()(e);
                          }
                        }}
                        className={`rt-resizer ${
                          header.column.getIsResizing() ? 'rt-resizer-is-resizing' : ''
                        }`}
                        aria-label="Redimensionar columna"
                      />
                    )}
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody>
            {table.getRowModel().rows.length === 0 ? (
              <tr>
                <td
                  colSpan={tanstackColumns.length}
                  style={{ textAlign: 'center', padding: '20px' }}
                >
                  {translations.noDataText}
                </td>
              </tr>
            ) : (
              table.getRowModel().rows.map((row) => {
                const trProps = getTrProps ? getTrProps({}, { original: row.original, row }) : {};
                return (
                  <tr key={row.id} {...trProps}>
                    {row.getVisibleCells().map((cell) => (
                      <td key={cell.id} style={{ width: cell.column.getSize() }}>
                        <div className="rt-table-cell-content">
                          {flexRender(cell.column.columnDef.cell, cell.getContext())}
                        </div>
                      </td>
                    ))}
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {showPaginationBottom && data.length > 0 && (
        <div className="rt-pagination">
          <button
            type="button"
            onClick={() => table.previousPage()}
            disabled={!table.getCanPreviousPage()}
            className="rt-pagination-button"
          >
            {translations.previousText}
          </button>
          <span className="rt-pagination-info">
            <span>{translations.pageText}</span>
            <input
              type="number"
              min="1"
              max={table.getPageCount()}
              value={table.getState().pagination.pageIndex + 1}
              onChange={(e) => {
                const page = e.target.value ? Number(e.target.value) - 1 : 0;
                const validPage = Math.min(Math.max(0, page), table.getPageCount() - 1);
                setPagination((prev) => ({ ...prev, pageIndex: validPage }));
              }}
              className="rt-pagination-page-input"
            />
            <span>
              {translations.ofText} {table.getPageCount()}
            </span>
            <span style={{ color: '#9ca3af', fontSize: '0.75rem', marginLeft: '4px' }}>
              ({data.length} total)
            </span>
          </span>
          <select
            value={table.getState().pagination.pageSize}
            onChange={(e) => {
              table.setPageSize(Number(e.target.value));
            }}
            className="rt-pagination-size-select"
          >
            {[10, 20, 50, 100].map((pageSize) => (
              <option key={pageSize} value={pageSize}>
                {pageSize} {translations.rowsText}
              </option>
            ))}
          </select>
          <button
            type="button"
            onClick={() => {
              if (table.getCanNextPage()) {
                table.nextPage();
              } else if (hasMore && onLoadMore) {
                onLoadMore();
              }
            }}
            disabled={!table.getCanNextPage() && (!hasMore || isLoadingMore)}
            className="rt-pagination-button"
          >
            {translations.nextText}
          </button>
          {hasMore && onLoadMore && !table.getCanNextPage() && (
            <button
              type="button"
              onClick={onLoadMore}
              disabled={isLoadingMore}
              className="rt-pagination-button"
              style={{
                fontSize: '0.7rem',
                padding: '4px 10px',
                marginLeft: '4px',
                opacity: isLoadingMore ? 0.6 : 1,
              }}
            >
              {isLoadingMore ? 'Cargando...' : 'Cargar más'}
            </button>
          )}
        </div>
      )}
    </div>
  );
};

Table.propTypes = {
  columns: PropTypes.arrayOf(
    PropTypes.shape({
      Header: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
      accessor: PropTypes.oneOfType([PropTypes.string, PropTypes.func]),
      Cell: PropTypes.func,
      CellComponent: PropTypes.elementType,
      cellProps: PropTypes.object,
      sortable: PropTypes.bool,
      filterable: PropTypes.bool,
      width: PropTypes.number,
    }),
  ),
  data: PropTypes.arrayOf(PropTypes.object),
  defaultPageSize: PropTypes.number,
  showPaginationBottom: PropTypes.bool,
  className: PropTypes.string,
  getTrProps: PropTypes.func,
  onLoadMore: PropTypes.func,
  isLoadingMore: PropTypes.bool,
  hasMore: PropTypes.bool,
};

export default Table;
